import Image from "next/image";
import { sanitize } from "isomorphic-dompurify";
import Link from "next/link";
import { motion, useAnimationControls } from "framer-motion";

import {
  LayoutType,
  OptionsType,
  DividerType,
  PageType,
  PagenationDataType,
} from "../../utils/baseTypes";
import { Section } from "../Global/Section";
import Arrow from "../../images/arrow_dark.svg";
import { getDataArray } from "../../utils/getData";
import { GetStaticPropsContext } from "next";
import { useState, useRef, useEffect } from "react";
import { getRelativePathFromLink } from "../../utils/url";
import Divider from "../Divider/Divider";
import { Button } from "../Global/Button";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useInView } from "react-intersection-observer";
import { unlinkWiki } from "../../utils/unlinkWiki";
import { CustomLink } from "../Global/CustomLink";
import { shuffle } from "lodash";
import { useRouter } from "next/router";
import { LoadMore } from "../Global/LoadMore";
import useUpdateEffect from "../../hooks/useUpdateEffect";
import { Pagenation } from "../Global/Pagenation";
import { LoadMorePagination } from "../Global/LoadMorePagination";
import { StickyLoader } from "../../Loader/Loader";
import useEmblaCarousel from "embla-carousel-react";
import { usePrevNextButtons } from "../../utils/usePrevNextButtons";

export type AutorenType = {
  acf_fc_layout: "authors";
  authors: {
    authors_type: "grid" | "slider";
    grid_typ: "pagination" | "load-more" | "pagination_load_more";
    headline: string;
    divider: DividerType;
    posts_per_page: string;
    posts_to_load: string;
    htype_tag: "h2" | "h3";
  };
  layout: LayoutType;
};

interface AutorenProps {
  data: AutorenType;
  AuthorsGridSlider: PagenationDataType;
  context: GetStaticPropsContext;
  options: OptionsType;
}

const transition_ = {
  ease: [0.455, 0.03, 0.515, 0.955],
  duration: 1,
};

const arrow_variant = {
  hidden: {
    x: -50,
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: transition_,
  },
};

const variants_grid = {
  hidden: {
    y: 50,
    opacity: 0,
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      ...transition_,
      delay: 0.5,
      staggerChildren: 0.5,
    },
  },
};

const variant_slide = {
  hidden: {
    x: 100,
    // opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: transition_,
  },
};

const Autoren = ({
  data,
  AuthorsGridSlider,
  context,
  options,
}: AutorenProps) => {
  const { abstand, background, id } = data.layout;
  const {
    divider,
    headline,
    posts_to_load: p_to_load,
    posts_per_page: p_per_page,
    authors_type,
    htype_tag,
    grid_typ,
  } = data.authors;

  const { query } = useRouter();
  const pagetype = query["pt"] ? query["pt"] : null;
  const page =
    pagetype === "autoren" ? (query["page"] ? Number(query["page"]) : 1) : 1;

  const posts_per_page = isNaN(Number(p_per_page)) ? 10 : Number(p_per_page);
  const posts_to_load = isNaN(Number(p_to_load)) ? 10 : Number(p_to_load);

  const gap = abstand === "default" ? "" : abstand;

  const { learn_more } = options.acf;

  const [autorinData, setautorinData] = useState<PageType[]>([]);
  const [prevHeight, setPrevHeight] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [animationState, setAnimationState] = useState<
    "loading" | "idle" | "hide" | "onhover" | "endhover"
  >("idle");

  const columnsRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const shuffled = shuffle(AuthorsGridSlider.posts);
    setautorinData(shuffled);

    if (authors_type === "grid" && grid_typ !== "load-more") {
      setPrevHeight(columnsRef.current?.offsetHeight ?? 0);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AuthorsGridSlider]);

  const hasPostsToLoad =
    grid_typ === "pagination" || grid_typ === "pagination_load_more"
      ? page * Number(posts_per_page) < AuthorsGridSlider.total_posts
      : autorinData.length < AuthorsGridSlider.total_posts;

  const loadMore = async () => {
    try {
      setAnimationState("loading");

      const loaded = autorinData.length;
      const isToShow =
        Number(posts_to_load) * (page - 1) === loaded
          ? null
          : Number(posts_to_load) * (page - 1);

      const fields = `&_fields=id,link,acf.header_autor.image,acf.header_autor.title,acf.header_autor.teaser_text,acf.header_autor.text,acf.header_autor.topics`;
      const { pageData, headers } = await getDataArray(
        `/autor?per_page=${
          isToShow ? isToShow : posts_to_load
        }&offset=${loaded}${fields}`,
        context.locale,
        context.defaultLocale
      );

      const total_posts = parseInt(headers.get("X-WP-Total")!);
      const newLoaded = Array.isArray(pageData) ? pageData.length : 0;

      if (Array.isArray(pageData)) {
        setautorinData((last) => last.concat(pageData));
      }

      if (total_posts === newLoaded + loaded) {
        setAnimationState("hide");
        return;
      }
    } catch (err) {
      console.error(err);
    }

    setAnimationState("idle");
    setLoading(false);
  };

  const loadMorePage = async () => {
    if (columnsRef.current) {
      setPrevHeight(columnsRef.current.clientHeight);
    }
    setLoading(true);
    const url = `/autor?per_page=${posts_per_page}&page=${page}`;

    const { pageData } = await getDataArray(
      url,
      context.locale,
      context.defaultLocale
    );

    setautorinData(pageData);

    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: "smooth",
      });
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  useUpdateEffect(() => {
    if (pagetype === null || pagetype === "autoren") {
      switch (grid_typ) {
        case "pagination":
        case "pagination_load_more":
          loadMorePage();
          break;
        case "load-more":
          loadMore();
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  let HeadlineJSX;
  switch (htype_tag) {
    case "h2":
      HeadlineJSX = (
        <h2 className="autoren-headline">
          <strong
            dangerouslySetInnerHTML={{ __html: sanitize(headline) }}
          ></strong>
        </h2>
      );
      break;
    case "h3":
      HeadlineJSX = (
        <h3 className="autoren-headline">
          <strong
            dangerouslySetInnerHTML={{ __html: sanitize(headline) }}
          ></strong>
        </h3>
      );
    default:
      HeadlineJSX = (
        <h2 className="autoren-headline">
          <strong
            dangerouslySetInnerHTML={{ __html: sanitize(headline) }}
          ></strong>
        </h2>
      );
      break;
  }

  let mainJSX;
  switch (authors_type) {
    case "grid":
      mainJSX = (
        <>
          <motion.div
            ref={columnsRef}
            style={{
              height: loading ? `${prevHeight}px` : "auto",
            }}
            className="columns is-multiline is-centered mt-6 px-4"
          >
            {loading && <StickyLoader />}
            {autorinData &&
              !loading &&
              autorinData.map((autor, i) => (
                <AutoronGridJSX key={i} autor={autor} learn_more={learn_more} />
              ))}
          </motion.div>
          <div className="columns is-multiline">
            {hasPostsToLoad && grid_typ === "load-more" && (
              <motion.div className="column is-12 loadmore-wrapper">
                <LoadMore
                  background={background}
                  pageType="autoren"
                  page={page + 1}
                  animationState={animationState}
                  setAnimationState={setAnimationState}
                />
              </motion.div>
            )}

            {grid_typ === "pagination" && (
              <Pagenation
                postsPerPage={posts_per_page}
                postsTotal={AuthorsGridSlider.total_posts}
                currentPage={page}
                pageType="autoren"
              />
            )}

            {grid_typ === "pagination_load_more" && (
              <LoadMorePagination
                postsPerPage={posts_per_page}
                postsTotal={AuthorsGridSlider.total_posts}
                currentPage={page}
                pageType="autoren"
                hasPostsToLoad={hasPostsToLoad}
                extraClass="mt-6"
              />
            )}
          </div>
        </>
      );
      break;
    case "slider":
      mainJSX = <AutorinSlider autorinData={autorinData} />;
      break;
    default:
      mainJSX = <></>;
      break;
  }

  return (
    <Section
      ref={sectionRef}
      className={`autoren divider  ${background} ${gap}`}
      id={id}
      disableColumns
      isClipped={false}
    >
      {divider && divider.divider_on && (
        <Divider data={divider} background={background} />
      )}
      {HeadlineJSX}
      {mainJSX}
    </Section>
  );
};

const AutorinSlideJSX = ({ autor }: { autor: PageType }) => {
  const isTablet = useMediaQuery("(max-width: 1023px)");

  const contentJSX = (
    <div className="content-wrapper">
      <div className="image-wrapper">
        {autor.acf?.header_autor?.image && (
          <figure className="image is-150x150">
            <Image
              className="is-rounded"
              src={autor.acf?.header_autor?.image.url as string}
              width={autor.acf?.header_autor?.image.width}
              height={autor.acf?.header_autor?.image.height}
              alt={autor.acf?.header_autor?.image.alt}
              data-title={autor.acf?.header_autor?.image.title}
              sizes="(max-width: 768px) 50vw, (max-width: 1023px) 25vw, (max-width: 1440px) 15vw, 10vw"
            />
          </figure>
        )}
      </div>
      <div className="autoren-slide-info">
        {autor.acf?.header_autor?.title && (
          <span>
            <strong
              dangerouslySetInnerHTML={{
                __html: sanitize(unlinkWiki(autor.acf.header_autor.title)),
              }}
            />
          </span>
        )}
        {autor.acf?.header_autor?.topics && (
          <span
            dangerouslySetInnerHTML={{
              __html: sanitize(unlinkWiki(autor.acf?.header_autor?.topics)),
            }}
          />
        )}
      </div>
    </div>
  );

  //? one is inside a link and other is not an <a> tag cant be a
  //? parent of another <a> tag soooo .....
  return (
    <>
      <motion.div className="autoren-slide">
        {isTablet && (
          <>
            {contentJSX}
            <Button
              className="is-hidden-desktop"
              type="primary"
              title="Zur Autorin"
              href={getRelativePathFromLink(autor.link)}
            />
          </>
        )}

        {!isTablet && (
          <CustomLink
            href={getRelativePathFromLink(autor.link)}
            className="autoren-link"
            target={"_self"}
            disablePrefetch
          >
            {contentJSX}
          </CustomLink>
        )}
      </motion.div>
    </>
  );
};

const AutoronGridJSX = ({
  autor,
  learn_more,
}: {
  autor: PageType;
  learn_more: string;
}) => {
  const grid_controls = useAnimationControls();
  const arrow_controls = useAnimationControls();
  const [ref, inView] = useInView();

  useEffect(() => {
    async function animtionSequence() {
      await grid_controls.start("visible");
      arrow_controls.start("visible");
    }
    if (inView) animtionSequence();
  }, [arrow_controls, grid_controls, inView]);
  return (
    <div ref={ref} className="column is-12 autoren-grid">
      <Link
        href={getRelativePathFromLink(autor.link)}
        prefetch={false}
        className="autoren-link"
      >
        <motion.div
          className="columns is-multiline autoren-grid-box"
          variants={variants_grid}
          initial="hidden"
          animate={grid_controls}
        >
          <div className="column is-4-tablet is-3-desktop">
            <div className="image-wrapper">
              {autor.acf?.header_autor?.image && (
                <figure className="image is-120x120">
                  <Image
                    className="is-rounded"
                    src={autor.acf?.header_autor?.image.url as string}
                    width={autor.acf?.header_autor?.image.width}
                    height={autor.acf?.header_autor?.image.height}
                    alt={autor.acf?.header_autor?.image.alt}
                    data-title={autor.acf?.header_autor?.image.title}
                    sizes="(max-width: 768px) 50vw, (max-width: 1023px) 25vw, (max-width: 1440px) 15vw, 10vw"
                  />
                </figure>
              )}
            </div>
          </div>
          <div className="column is-8-tablet is-7-desktop">
            <h3>
              <strong
                dangerouslySetInnerHTML={{
                  __html: sanitize(autor.acf?.header_autor?.title ?? ""),
                }}
              />
            </h3>
            <div
              className="text"
              dangerouslySetInnerHTML={{
                __html: sanitize(
                  autor.acf?.header_autor?.teaser_text
                    ? unlinkWiki(autor.acf?.header_autor?.teaser_text)
                    : unlinkWiki(autor.acf?.header_autor?.text!),
                  {
                    ADD_ATTR: ["target"],
                  }
                ),
              }}
            ></div>
            <div className="arrow is-hidden-desktop">
              <span>{learn_more}</span>
              <Arrow width={22.06} height={14.58} />
            </div>
          </div>
          <div className="column is-hidden-touch is-2-desktop">
            <motion.div
              variants={arrow_variant}
              animate={arrow_controls}
              initial={"hidden"}
              className="arrow-wrapper"
            >
              <div className="arrow">
                <span className="arrow-text">{learn_more}</span>
                <Arrow width={22.06} height={14.58} />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Link>
      {/* below div's are for gradient corner on hover */}
      <div className="corner-div" />
      <div className="corner-div" />
    </div>
  );
};

const AutorinSlider = ({ autorinData }: { autorinData: PageType[] }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    dragFree: true,
    align: "start",
    watchSlides: true,
  });

  const {
    nextBtnDisabled,
    prevBtnDisabled,
    onNextButtonClick,
    onPrevButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <div className="autoren-slider">
      <div ref={emblaRef} className="embla-slider">
        <ul className="embla-container">
          {autorinData &&
            autorinData.map((autor, i) => (
              <li className="embla-slide" key={i}>
                <AutorinSlideJSX autor={autor} />
              </li>
            ))}
        </ul>
      </div>
      <div className="autoren-slider-navigation">
        <div>
          <motion.button
            initial={{
              rotate: 180,
            }}
            whileTap={{ scale: 0.8 }}
            className="prev"
            aria-label="prev"
            disabled={prevBtnDisabled}
            onClick={onPrevButtonClick}
          >
            <Arrow width={22.06} height={14.58} />
          </motion.button>
        </div>
        <div>
          <motion.button
            whileTap={{ scale: 0.8 }}
            whileInView={{
              x: [0, 50, 0],
              // scale: [1, 1.3, 1],
              transition: {
                ...transition_,
                delay: 1,
              },
            }}
            className="next"
            aria-label="next"
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          >
            <Arrow width={22.06} height={14.58} />
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default Autoren;
